import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { 
  BarChart3, 
  FileText, 
  Users, 
  Settings, 
  LogOut,
  X,
  Megaphone,
  Zap,
  Bell,
  ClipboardList,
  Building2,
  Shield,
  ChevronDown,
  ChevronRight,
  Plus,
  TrendingUp,
  Activity,
  MapPin,
  DollarSign,
  Clock,
  Target,
  Calendar,
  Eye,
  Edit,
  Database
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { UserRole } from '../../types';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface MenuItem {
  path: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: string;
  description?: string;
  adminOnly?: boolean;
  editorAccess?: boolean;
  children?: MenuItem[];
}

const menuItems: MenuItem[] = [
  { 
    path: '/dashboard', 
    label: 'Dashboard', 
    icon: BarChart3,
    description: 'Overview & Analytics'
  },
  {
    path: '/works',
    label: 'Works Management',
    icon: Building2,
    description: 'Construction Works',
    editorAccess: true,
    children: [
      {
        path: '/works/dashboard',
        label: 'Works Dashboard',
        icon: BarChart3,
        description: 'Works Overview',
        editorAccess: true
      },
      {
        path: '/works/list',
        label: 'All Works',
        icon: FileText,
        description: 'Manage Works',
        editorAccess: true
      },
      {
        path: '/works/create',
        label: 'Add New Work',
        icon: Plus,
        description: 'Create Work',
        editorAccess: true
      },
      {
        path: '/works/analytics',
        label: 'Analytics',
        icon: TrendingUp,
        description: 'Performance Insights',
        editorAccess: true
      },
      {
        path: '/works/reports',
        label: 'Reports',
        icon: Activity,
        description: 'Progress Reports',
        editorAccess: true,
        children: [
          {
            path: '/works/reports/districts',
            label: 'Districts Drill-down',
            icon: MapPin,
            description: 'District-wise Analysis',
            editorAccess: true
          },
          {
            path: '/works/reports/financial',
            label: 'Financial Reports',
            icon: DollarSign,
            description: 'Budget & Expenditure',
            editorAccess: true
          },
          {
            path: '/works/reports/progress',
            label: 'Progress Reports',
            icon: Clock,
            description: 'Work Progress Tracking',
            editorAccess: true
          },
          {
            path: '/works/reports/performance',
            label: 'Performance Analysis',
            icon: Target,
            description: 'Contractor Performance',
            editorAccess: true
          }
        ]
      }
    ]
  },
  {
    path: '/masters',
    label: 'Master Data',
    icon: Database,
    description: 'System Configuration',
    adminOnly: true,
    children: [
      {
        path: '/masters',
        label: 'Overview',
        icon: BarChart3,
        description: 'Master Data Dashboard',
        adminOnly: true
      }
    ]
  },
  { 
    path: '/flash-news', 
    label: 'Flash News', 
    icon: Zap,
    description: 'Urgent Announcements',
    badge: 'Hot',
    editorAccess: true
  },
  { 
    path: '/notices', 
    label: 'Notice Board', 
    icon: ClipboardList,
    description: 'Official Notices & Circulars',
    badge: 'New',
    editorAccess: true
  },
  { 
    path: '/content', 
    label: 'Content Management', 
    icon: FileText,
    description: 'Articles & Press Releases',
    editorAccess: true
  },
  { 
    path: '/projects', 
    label: 'Projects', 
    icon: Building2,
    description: 'Engineering Projects',
    editorAccess: true
  },
  { 
    path: '/users', 
    label: 'User Management', 
    icon: Users,
    description: 'Staff & Permissions',
    adminOnly: true
  },
  { 
    path: '/settings', 
    label: 'Settings', 
    icon: Settings,
    description: 'System Configuration',
    adminOnly: true
  },
];

export const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const { user, logout } = useAuth();
  const [expandedItems, setExpandedItems] = useState<string[]>(['/works', '/works/reports']);


  const toggleExpanded = (path: string) => {
    setExpandedItems(prev => 
      prev.includes(path) 
        ? prev.filter(p => p !== path)
        : [...prev, path]
    );
  };

  // Filter menu items based on user role
  const filteredMenuItems = menuItems.filter(item => {
    if (!user) return false;
    
    // Admin can access everything
    if (user.role === UserRole.SYSTEM_ADMIN) return true;
    
    // Editor access
    if (user.role === UserRole.EDITOR || user.role === UserRole.EXECUTIVE_ENGINEER || user.role === UserRole.ASSISTANT_ENGINEER) {
      return !item.adminOnly;
    }
    
    // Viewer access (only dashboard and viewing content)
    if (user.role === UserRole.VIEWER) {
      return !item.adminOnly && !item.editorAccess;
    }
    
    return false;
  });

  const getRoleDisplay = (role: UserRole): { label: string; color: string } => {
    const roleMap: Record<UserRole, { label: string; color: string }> = {
      [UserRole.SYSTEM_ADMIN]: { label: 'System Admin', color: 'bg-red-400' },
      [UserRole.ENGINEER_IN_CHIEF]: { label: 'Engineer-in-Chief', color: 'bg-purple-400' },
      [UserRole.EXECUTIVE_ENGINEER]: { label: 'Executive Engineer', color: 'bg-blue-400' },
      [UserRole.ASSISTANT_ENGINEER]: { label: 'Assistant Engineer', color: 'bg-green-400' },
      [UserRole.EDITOR]: { label: 'Editor', color: 'bg-yellow-400' },
      [UserRole.VIEWER]: { label: 'Viewer', color: 'bg-gray-400' },
    };
    
    return roleMap[role] || { label: role, color: 'bg-gray-400' };
  };

  const roleInfo = user ? getRoleDisplay(user.role) : { label: 'User', color: 'bg-gray-400' };

  const renderMenuItem = (item: MenuItem, depth: number = 0) => {
    const Icon = item.icon;
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.includes(item.path);
    const paddingLeft = depth === 0 ? 'pl-4' : depth === 1 ? 'pl-8' : 'pl-12';
  
    return (
      <div key={item.path}>
        {hasChildren ? (
          <button
            onClick={() => toggleExpanded(item.path)}
            className={`group w-full flex items-center justify-between px-4 py-3 text-sm font-medium rounded-xl transition-all duration-200 text-gray-600 hover:bg-gray-50 hover:text-gray-900 ${paddingLeft}`}
          >
            <div className="flex items-center">
              <Icon className="w-5 h-5 mr-4 flex-shrink-0 text-gray-400 group-hover:text-gray-600" />
              <div className="flex-1 min-w-0 text-left">
                <div className="flex items-center justify-between">
                  <span className="truncate">{item.label}</span>
                  {item.badge && (
                    <span className={`ml-2 px-2 py-0.5 text-xs font-medium rounded-full ${
                      item.badge === 'Hot' 
                        ? 'bg-red-100 text-red-600 group-hover:bg-red-200'
                        : item.badge === 'New'
                        ? 'bg-green-100 text-green-600 group-hover:bg-green-200'
                        : 'bg-blue-100 text-blue-600 group-hover:bg-blue-200'
                    }`}>
                      {item.badge}
                    </span>
                  )}
                </div>
                {item.description && (
                  <div className="text-xs mt-0.5 truncate text-gray-400">
                    {item.description}
                  </div>
                )}
              </div>
            </div>
            {isExpanded ? (
              <ChevronDown className="w-4 h-4 text-gray-400" />
            ) : (
              <ChevronRight className="w-4 h-4 text-gray-400" />
            )}
          </button>
        ) : (
          <NavLink
            to={item.path}
            onClick={onClose}
            className={({ isActive }) =>
              `group w-full flex items-center ${paddingLeft} pr-4 py-3 text-sm font-medium rounded-xl transition-all duration-200 relative ${
                isActive
                  ? 'bg-gradient-to-r from-blue-50 to-blue-100 text-blue-700 shadow-sm border-l-4 border-blue-600'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`
            }
          >
            {({ isActive }) => (
              <>
                <Icon className={`w-5 h-5 mr-4 flex-shrink-0 transition-colors ${
                  isActive ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-600'
                }`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <span className="truncate">{item.label}</span>
                    {item.badge && (
                      <span className={`ml-2 px-2 py-0.5 text-xs font-medium rounded-full ${
                        item.badge === 'Hot' 
                          ? (isActive ? 'bg-red-600 text-white' : 'bg-red-100 text-red-600 group-hover:bg-red-200')
                          : item.badge === 'New'
                          ? (isActive ? 'bg-green-600 text-white' : 'bg-green-100 text-green-600 group-hover:bg-green-200')
                          : (isActive ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-600 group-hover:bg-blue-200')
                      }`}>
                        {item.badge}
                      </span>
                    )}
                  </div>
                  {item.description && (
                    <div className={`text-xs mt-0.5 truncate ${
                      isActive ? 'text-blue-600' : 'text-gray-400'
                    }`}>
                      {item.description}
                    </div>
                  )}
                </div>
              </>
            )}
          </NavLink>
        )}
        
        {/* Render children if expanded */}
        {hasChildren && isExpanded && (
          <div className={`mt-2 space-y-1 ${depth === 0 ? 'ml-4 border-l-2 border-gray-100 pl-4' : 'ml-6 border-l border-gray-200 pl-3'}`}>
            {item.children?.map(child => renderMenuItem(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-72 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:relative lg:translate-x-0 lg:flex lg:flex-col lg:w-72 ${
          isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-6 bg-gradient-to-r from-blue-600 to-blue-700 flex-shrink-0">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center mr-3 shadow-sm">
                <span className="text-blue-600 font-bold text-sm">PRED</span>
              </div>
              <div>
                <span className="text-xl font-bold text-white">PRED Admin</span>
                <div className="text-xs text-blue-100">Telangana State</div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="lg:hidden text-white hover:text-blue-200 p-1 rounded-md transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
            {filteredMenuItems.map(item => renderMenuItem(item))}
          </nav>

          {/* User Info */}
          <div className="px-4 py-4 border-t border-gray-200 flex-shrink-0">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm">
                <span className="text-white font-semibold text-lg">
                  {user?.name?.charAt(0)?.toUpperCase() || 'A'}
                </span>
              </div>
              <div className="ml-4 flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">{user?.name || 'Admin User'}</p>
                <p className="text-xs text-gray-500 flex items-center">
                  <span className={`w-2 h-2 rounded-full mr-2 ${roleInfo.color}`}></span>
                  {roleInfo.label}
                </p>
                {user?.department && (
                  <p className="text-xs text-gray-400 truncate">
                    {user.department}
                  </p>
                )}
                {user?.employee_id && (
                  <p className="text-xs text-gray-400 font-mono">
                    ID: {user.employee_id}
                  </p>
                )}
              </div>
              <button
                onClick={logout}
                className="text-gray-400 hover:text-red-600 transition-colors duration-200 flex-shrink-0 p-2 rounded-lg hover:bg-red-50"
                title="Logout"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
            
            {/* User Status Indicator */}
            <div className="mt-3 px-3 py-2 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></div>
                  <span className="text-xs font-medium text-green-700">Online</span>
                </div>
                <div className="flex items-center">
                  {user?.role === UserRole.SYSTEM_ADMIN && (
                    <Shield className="w-3 h-3 text-green-600 mr-1" />
                  )}
                  <span className="text-xs text-green-600">Active</span>
                </div>
              </div>
            </div>

            {/* User Division/Circle Info */}
            {user && (user.division || user.circle || user.zone) && (
              <div className="mt-2 px-3 py-2 bg-blue-50 rounded-lg border border-blue-200">
                <div className="text-xs text-blue-700 space-y-1">
                  {user.division && <div>Division: {user.division}</div>}
                  {user.circle && <div>Circle: {user.circle}</div>}
                  {user.zone && <div>Zone: {user.zone}</div>}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-2 border-t border-gray-100">
            <div className="text-center">
              <p className="text-xs text-gray-400">
                PRED CMS v2.1
              </p>
              <p className="text-xs text-gray-300">
                Panchayat Raj Engineering Dept.
              </p>
              <div className="flex items-center justify-center mt-1">
                <div className="w-1 h-1 bg-green-400 rounded-full mr-1"></div>
                <p className="text-xs text-gray-300">
                  System Operational
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};